/**
 * WeeklyRecapBanner — P5 + Milestones cumulatifs (iter 183)
 * Encart chiffré « Cette semaine chez Homly » au sommet du cockpit.
 * - Récupère /cockpit/weekly-recap et affiche les 3-5 signaux positifs.
 * - Affiche en premier les milestones cumulatifs (paliers franchis
 *   cette semaine) avec un design "trophée" doré distinctif.
 * - Dismissable jusqu'au lundi suivant (cooldown côté backend).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Sparkles, Star, FileCheck, ShieldCheck, Inbox, Share2, Send, Clock,
    X, Loader2, Award, Trophy,
} from 'lucide-react';
import api from '../../lib/api';
import MilestoneShareDialog from './MilestoneShareDialog';

const ICONS = {
    star: Star,
    'file-check': FileCheck,
    'shield-check': ShieldCheck,
    inbox: Inbox,
    'share-2': Share2,
    send: Send,
    clock: Clock,
    award: Award,
    trophy: Trophy,
};

function formatMinutes(m) {
    if (m == null) return '—';
    if (m < 60) return `${m} min`;
    const h = Math.floor(m / 60);
    const rest = m % 60;
    return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

function formatValue(h) {
    if (h.format === 'minutes') return formatMinutes(h.value);
    return new Intl.NumberFormat('fr-FR').format(h.value || 0);
}

function pluralize(h) {
    const label = h.value > 1 ? (h.plural || h.label) : (h.singular || h.label);
    return label;
}

export default function WeeklyRecapBanner({ scope = 'mine' }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dismissing, setDismissing] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [shareMilestone, setShareMilestone] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/cockpit/weekly-recap?scope=${scope}`);
            setData(r.data);
        } catch (e) {
            // Silencieux — l'encart est facultatif
        } finally { setLoading(false); }
    }, [scope]);

    useEffect(() => { load(); }, [load]);

    const dismiss = async () => {
        setDismissing(true);
        setHidden(true);
        try { await api.post('/cockpit/weekly-recap/dismiss'); }
        catch (e) { /* silencieux */ }
        setDismissing(false);
    };

    if (loading || !data || !data.should_show || hidden) return null;

    return (
        <section
            data-testid="weekly-recap-banner"
            className="rounded-2xl p-4 sm:p-5 relative overflow-hidden mb-4 border"
            style={{
                background: 'linear-gradient(135deg, rgba(104,23,147,0.18) 0%, rgba(255,145,77,0.14) 100%)',
                borderColor: 'rgba(255,145,77,0.35)',
            }}
        >
            <button
                type="button"
                onClick={dismiss}
                disabled={dismissing}
                aria-label="Masquer jusqu'à lundi"
                data-testid="weekly-recap-dismiss"
                className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/10 transition text-white/70"
            >
                {dismissing ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
            </button>

            <div className="flex items-start gap-3 pr-8 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                     style={{
                         background: 'linear-gradient(135deg, #681793, #FF914D)',
                         color: '#fff',
                     }}>
                    <Sparkles className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#FF914D]">
                        Cette semaine chez Homly
                    </p>
                    <p className="text-sm mt-0.5 text-foreground">
                        {scope === 'agency'
                            ? "Votre agence a été active — voici ce qui a bougé cette semaine."
                            : "Votre semaine en un coup d'œil — ce que vous avez accompli."}
                    </p>
                </div>
            </div>

            {/* Milestones cumulatifs — paliers franchis cette semaine */}
            {(data.milestones || []).length > 0 && (
                <ul className="space-y-2 mb-3" data-testid="weekly-recap-milestones">
                    {data.milestones.map((m) => {
                        const Icon = ICONS[m.icon] || Trophy;
                        return (
                            <li key={m.code}
                                data-testid={`weekly-recap-milestone-${m.code}`}
                                className="flex items-start gap-2.5 rounded-lg p-2.5 border backdrop-blur-sm"
                                style={{
                                    background: 'linear-gradient(90deg, rgba(255,193,7,0.18), rgba(255,145,77,0.10) 60%, transparent)',
                                    borderColor: 'rgba(255,193,7,0.45)',
                                }}>
                                <span className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                      style={{
                                          background: 'linear-gradient(135deg, #F5B301, #FF914D)',
                                          color: '#fff',
                                          boxShadow: '0 4px 12px rgba(245,179,1,0.35)',
                                      }}>
                                    <Icon className="w-4 h-4" strokeWidth={2.5} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[10px] font-bold tracking-widest uppercase text-[#F5B301]">
                                        Palier franchi
                                    </p>
                                    <p className="text-sm font-semibold text-foreground leading-tight mt-0.5">
                                        {m.label}
                                    </p>
                                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                                        {m.message}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShareMilestone(m)}
                                    aria-label={`Partager le palier ${m.label}`}
                                    data-testid={`weekly-recap-milestone-share-${m.code}`}
                                    className="shrink-0 self-center inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#2A0A3D] transition hover:opacity-90"
                                    style={{ background: 'linear-gradient(135deg, #F5B301, #FF914D)' }}
                                >
                                    <Share2 className="w-3.5 h-3.5" />
                                    Partager
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <ul className="flex flex-wrap gap-2" data-testid="weekly-recap-highlights">
                {(data.highlights || []).map((h) => {
                    const Icon = ICONS[h.icon] || Sparkles;
                    const isTime = h.code === 'minutes_saved';
                    return (
                        <li key={h.code}
                            data-testid={`weekly-recap-${h.code}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border backdrop-blur-sm"
                            style={{
                                background: isTime ? 'rgba(22,163,74,0.18)' : 'rgba(255,255,255,0.08)',
                                borderColor: isTime ? 'rgba(22,163,74,0.45)' : 'rgba(255,145,77,0.35)',
                                color: 'var(--foreground)',
                            }}
                            title={h.hint || ''}>
                            <Icon className="w-3.5 h-3.5"
                                  style={{ color: isTime ? '#4ade80' : '#FF914D' }} />
                            <span className="font-bold tabular-nums text-foreground">{formatValue(h)}</span>
                            <span className="text-xs text-muted-foreground">
                                {pluralize(h)}
                            </span>
                        </li>
                    );
                })}
            </ul>

            <MilestoneShareDialog
                milestone={shareMilestone}
                open={!!shareMilestone}
                onClose={() => setShareMilestone(null)}
            />
        </section>
    );
}
